import React from 'react';
import { faReceipt } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
// Create: src/assets/images/blogs/ontology-tax.jpg
import ontologyTaxImg from '../../../assets/images/blogs/Ontology-Tax.png';

const OntologyTax = () => (
    <BlogPost
        title="AI Shortcuts the Ontology Tax"
        subtitle="Semantic graphs are awesome and needed in an AI world, but we shouldn't have to pay a large tax to adopt them."
        category="Strategy"
        categoryIcon={faReceipt}
        color="#003366"
        date="March 2026"
        readTime="5 min read"
        image={ontologyTaxImg}
        imageAlt="Shortcutting the ontology tax with AI-native semantics"
        ctaHeading="Ready to skip the ontology tax?"
        ctaText="Book a discovery call and we'll show you how to get semantic capabilities without the months of upfront work."
        seoDescription="Why traditional ontologies cost $500K-$1.5M+ over five years and how AI-native semantics eliminates the ontology tax entirely."
        seoKeywords="ontology tax, knowledge graphs, semantic AI, Nebulyx Cognition, AI semantics, knowledge management"
        slug="ontology-tax"
    >
        <p>
            Saturday reflections. 🤔
        </p>

        <p>
            Ontologies and knowledge graphs are so hot right now and I'm always keen to
            read good posts about the realities of implementing them.
        </p>

        <p>
            As such, I was intrigued to read about "The Ontology Tax: What Nobody Tells
            You About the Real Cost of Knowledge Graphs."
        </p>

        <h3>The Staggering Scale</h3>

        <p>
            The headline: <strong>Typical cost between $500K-$1.5M+ over five years for
            a single domain ontology.</strong> Months of expert work before usage with
            15-25% annual maintenance. With governance adding 30-50%.
        </p>

        <p>
            Only 27% of orgs get knowledge graphs to production despite a $6.9B market.
        </p>

        <p>
            The SNOMED CT initiative has approximately 350,000+ clinical concepts and
            requires an entire non-profit organisation just to maintain it with monthly
            releases.
        </p>

        <p>
            Semantics are super important for accurate and robust information-based
            decisioning systems, especially if one is using AI.
        </p>

        <blockquote className="blog-post-quote">
            "But with traditional manual approaches, typically there is this massive effort
            needed before any use-cases can be even started and also to get up-to-date."
        </blockquote>

        <h3>The Same Pain</h3>

        <p>
            In my work building these types of systems I have found the same thing. Spending
            weeks trying to classify domain knowledge, build taxonomies and align definitions.
            Every time I added to the graph it became more brittle.
        </p>

        <p>
            A compliance process step isn't an obligation or prohibition - the ontology had
            no box for it, so I spent ages trying to shoehorn into compromise approaches.
        </p>

        <h3>A Different Path</h3>

        <p>
            This is what led me to creating a new type of small language model (now Nebulyx
            Cognition) that discovers semantic structure directly from language.
        </p>

        <p>
            It learns that "shall report all transactions" and "clinicians should offer CBT"
            share the same shape - someone must do something with a degree of authority.
        </p>

        <p>
            A really cool part is that business users can adjust, add and maintain through
            simple user interfaces. I.e. we don't require armies of knowledge specialists.
        </p>

        <p>
            <strong>But the grunt work is done by the model - encode 70-80% automatically
            and directly from source material.</strong>
        </p>

        <h3>The Results</h3>

        <p>
            When one queries e.g. "what are the medical procedures for X" - the actual text
            and location from the medical book is returned, every time. We can constantly
            and quickly update as new information comes in. A change in procedure or policy,
            a new regulation.
        </p>

        <p>
            Plus, link and do differences between text by querying the AI - which is very
            tough to do with traditional methods.
        </p>

        <p>
            All within minutes. Knowledge compounds through this agile flywheel effect
            rather than drifting despite it.
        </p>

        <h3>The Bottom Line</h3>

        <p>
            <strong>Semantic graphs (including Ontologies and Knowledge Graphs) are awesome
            and needed in an AI world, but we shouldn't have to pay a large tax to adopt
            them.</strong> 🎯
        </p>
    </BlogPost>
);

export default OntologyTax;
